import { Component, ContentChild, ElementRef, EventEmitter, inject, Input, OnChanges, OnInit, Output, QueryList, SimpleChanges, TemplateRef, ViewChild, ViewChildren } from '@angular/core';
import { DSVRowString, ScaleSequential, groups, scaleLinear } from 'd3';
import { ChartPopupDirective } from '../../directives/chart-popup.directive';
import { DashboardFilterController } from '../../filters/dashboard_filter_controller';
import chroma, { Color, Scale } from 'chroma-js';
import { LayoutService } from 'src/app/layout/service/app.layout.service';
import { geDataStatus } from 'src/app/shared/utils/get-data-status';
import { ChartRowColumnType } from 'src/app/shared/model/chart-row-column.type';
import { ChartRowClickEvent, ChartRowEvent, ChartRowHoverEvent, ChartRowUnhoverEvent } from 'src/app/shared/model/chart-row-event.interface';
import { fromEvent, merge, takeUntil } from 'rxjs';
import { DestroyStreamComponent } from 'src/app/shared/components/destroy-stream/destroy-stream.component';
import { calcScaleLinearDomain } from 'src/app/shared/utils/calc-scale-linear-domain';
import { PopupController } from '../../popups/popup-controller';
import { MapSidebarService } from '../../services/map-sidebar.service';
import { ChartTransitionScheduler } from '../chart-transition-scheduler';

export const RowChartValueKeys = {
  value: 'value',
  valueSharePct: 'value_share_pct',
}

@Component({
  selector: 'app-chart-row',
  templateUrl: './chart-row.component.html',
  styleUrls: ['./chart-row.component.scss'],
  providers: [ChartTransitionScheduler],
})
export class ChartRowComponent extends DestroyStreamComponent {

  @Input({ required: true }) filterController!: DashboardFilterController
  @Input({ required: true }) data!: any[] | null
  @Input() isLoading?: boolean
  @Input() error?: string | null
  @Input() valueKey = RowChartValueKeys.value;
  @Input() valuePctKey = RowChartValueKeys.valueSharePct;
  @Input({ required: true }) valueLabelKey!: string;
  @Input() rankKey = 'rank';
  @Input({ required: true }) rowIdKey!: string
  @Input() multiSelectName: string | null = null
  @Input() multiSelectValueKey: string | null = null
  @Input() height: string = '550px'
  @Input() fillScale: Scale<Color> | ScaleSequential<string, never> = chroma.scale(['#C8E3F2', '#6081A2'])
  @Input() skipFillScaleUpdate = false
  @Input() striped = true
  @Input() showId = false
  @Input() showRank = true
  @Input() showName = true
  @Input() rankColWidth = 28 // px
  @Input() idColWidth = 70 // px
  @Input() nameColWidth = 185 // px
  @Input() valueColWidth = 130 // px
  @Input() yPadding = '0.5rem'
  @Input() multiline = false
  @Input() idFormatter?: (name: string) => string
  @Input() nameFormatter?: (name: string, dataLength: number) => string
  @Input() selectedRow: number | string | null = null;
  @Input() selectedRowIndex: number | null = null;
  @Input() hoveredRow: number | string | null = null;
  @Input() hoveredRowIndex: number | null = null;
  @Input() useStaticBarHeight = false
  @Input() staticBarHeight = '32px'
  @Input() styleClass: string | null = null
  @Input() nameStyleClass: string | null = null
  @Input() barStyleClass: string | null = null
  @Input() valueStyleClass: string | null = null
  @Input() animation = true
  @Input() rowSlider = false
  @ContentChild(ChartPopupDirective) popup: ChartPopupDirective | null = null;
  @ContentChild('value') valueTemplate: TemplateRef<any> | null = null;
  @ContentChild('valuePct') valuePctTemplate: TemplateRef<any> | null = null;
  @ContentChild('rank') rankTemplate: TemplateRef<any> | null = null;

  @ViewChild('chartContainer') chartContainer!: ElementRef<HTMLDivElement>

  @Output() onRowClick = new EventEmitter<ChartRowClickEvent>()
  @Output() onRowSelected = new EventEmitter<ChartRowClickEvent>()
  @Output() onRowUnselected = new EventEmitter<ChartRowClickEvent>()
  @Output() onRowHover = new EventEmitter<ChartRowHoverEvent>()
  @Output() onRowHoverMove = new EventEmitter<ChartRowHoverEvent>()
  @Output() onRowUnhover = new EventEmitter<ChartRowUnhoverEvent>()

  width = 1160
  widthScale = scaleLinear();
  selectedCol: ChartRowColumnType | null = null;
  popupController = inject(PopupController, { optional: true }) ?? new PopupController();
  chartData: any[] = []
  multiSelectValue: string[] | null = null
  multiSelectValuePrev: string[] | null = null
  multiSelectParentValue: number | null = null
  rankIndex: { [k: string]: number } | null = null
  hadMultiSelectValueChanged = false

  get multiSelectFilter() {
    if (!this.multiSelectName) {
      return null
    }
    return this.filterController.getFilterByName(this.multiSelectName);
  }

  get multiSelectParentFilter() {
    if (!this.multiSelectFilter || !this.multiSelectFilter.parentFilter) {
      return null
    }
    return this.filterController.getFilterByName(this.multiSelectFilter.parentFilter.name);
  }

  get xRange() {
    // 20 is scrollbar width (if overflow-y happens)
    return [0, this.width - (this.showRank ? this.rankColWidth : 0) - (this.showId ? this.idColWidth : 0) - this.nameColWidth - this.valueColWidth - 20]
  }

  constructor(
    private layoutService: LayoutService,
    private mapSidebarService: MapSidebarService,
    private transitionScheduler: ChartTransitionScheduler,
  ) {
    super()
  }

  ngOnInit(): void {
    merge(
      this.layoutService.staticMenuDesktopInactive$,
      this.mapSidebarService.isOpen$,
    )
    .pipe(takeUntil(this.destroy$))
    .subscribe((val) => {
      setTimeout(() => {
        this.resize()
      }, 250)
    })

    fromEvent(window, 'resize')
      .pipe(takeUntil(this.destroy$))
      .subscribe(($event) => {
        this.resize()
      });
  }

  ngAfterViewInit() {
    setTimeout(() => {
      this.resize()
    })
  }

  @Input() maxFillValueDomain?: number

  ngOnChanges(changes: SimpleChanges) {
    const oldData: any[] | null | undefined = changes['data']?.previousValue;
    const newData: any[] | null | undefined = changes['data']?.currentValue;
    const dataStatus = geDataStatus(newData, oldData, this.rowIdKey)

    if (this.multiSelectFilter) {
      if (this.multiSelectValue) {
        this.multiSelectValuePrev = [...this.multiSelectValue]
      }

      this.multiSelectValue = this.getMultiSelectValue()

      // Set the flag to indicate if the user has interacted with multiselect at least once
      // This is used to determine if the chart should be animated when the newData is longer than the oldData
      this.hadMultiSelectValueChanged = this.multiSelectValuePrev?.length !== this.multiSelectValue?.length
    }

    if (newData) {
      this.updateRowSliderState(newData);

      const valueRange = calcScaleLinearDomain(newData, this.valueKey)
      const fillValueRange = this.maxFillValueDomain ? [valueRange[0], this.maxFillValueDomain] : valueRange

      if (!this.skipFillScaleUpdate) {
        this.fillScale.domain(fillValueRange!)
      }

      this.widthScale.domain([0, valueRange[1]])

      const newBarChartData = this.decorateWithRectData(newData)
      if (dataStatus === 'IS_FIRST_SET' || dataStatus === 'SAME_LENGTH_DIFFERENT_ITEMS' || !this.animation) {
        this.setChartData(newBarChartData)
      } else if (dataStatus === 'MORE_LENGTH' && !this.hadMultiSelectValueChanged) {
        this.setChartData(newBarChartData)
      } else if (dataStatus === 'SAME_LENGTH_SAME_ITEMS') {
        this.updateChartItems(newBarChartData)
      } else if (dataStatus === 'MORE_LENGTH') {
        this.addChartItem(newBarChartData)
      } else if (dataStatus === 'LESS_LENGTH') {
        this.removeChartItem(newBarChartData)
      }
    }

    if (changes['selectedRow']) {
      const tempSelectedRowIndex = this.chartData.findIndex(d => d[this.rowIdKey] == this.selectedRow);
      this.selectedRowIndex = tempSelectedRowIndex !== -1 ? tempSelectedRowIndex : null;
    }

  }

  private getMultiSelectValue(): string[] | null {
    if (!this.multiSelectFilter) return null

    // Due to filter value update timing issue on multiselect that has parent then we should derive the value from the data itself
    if (this.multiSelectFilter.parentFilter?.name) {
      return groups(this.data || [], d => d[this.multiSelectValueKey!]).map(group => group[0])
    }

    return this.multiSelectFilter.value;
  }

  resize() {
    this.width = this.chartContainer.nativeElement.clientWidth
    this.widthScale.range(this.xRange)

    if (this.data && this.data.length) {
      this.updateChartItems(this.decorateWithRectData(this.data))
    }
  }

  setChartData(data: any[]) {
    this.transitionScheduler.cancel()
    this.chartData = [...data]
    this.indexRank(data)
  }

  addChartItem(newData: any[]) {
    this.transitionScheduler.cancel()

    for (let i = 0; i < newData.length; i++) {
      const newItem = newData[i];

      if (this.multiSelectValuePrev?.includes(newItem[this.multiSelectValueKey!])) {
        continue;
      }

      const newItemPlaceholder = {
        ...newItem,
        height: 0,
        opacity: 0
      }

      // Insert a placeholder for the new item
      this.chartData.splice(i, 0, newItemPlaceholder)
    }

    // Reveal the new item and adjust the rest
    this.transitionScheduler.queue(() => {
      for (let i = 0; i < newData.length; i++) {
        const newItem = newData[i];
        const oldItem = this.chartData[i]

        Object.assign(oldItem, newItem);
      }
      this.indexRank(newData)
    }, 15);

  }

  updateChartItems(newData: any[]) {
    this.transitionScheduler.cancel()

    for (let i = 0; i < newData.length; i++) {
      const newItem = newData[i];
      const oldItem = this.chartData.find(oldItem => oldItem[this.valueLabelKey] === newItem[this.valueLabelKey])

      const rankMove = Number(newItem['rank']) - this.rankIndex![newItem[this.valueLabelKey]]

      Object.assign(oldItem, {
        ...newItem,
        transform: `translateY(${rankMove * 100}%)`,
        stripeColor: (i - rankMove) % 2 === 0 ? '#fff' : '#f5f5f5'
      })
    }

    // Sync up the display data and the source
    this.transitionScheduler.queue(() => {
      this.chartData = newData
      this.indexRank(newData)
    }, 715);

  }

  removeChartItem(newData: any[]) {
    this.transitionScheduler.cancel()

    for (const oldItem of this.chartData) {
      const newItem = newData.find(newItem => oldItem[this.valueLabelKey] === newItem[this.valueLabelKey])

      if (!newItem) {
        // Initially hide the removed item
        oldItem['height'] = 0
        oldItem['opacity'] = 0
      } else {
        // Adjust the remaining items
        Object.assign(oldItem, newItem);
      }
    }

    this.transitionScheduler.queue(() => {
      // Completely remove item by assigning the new data to the old one
      // Now the current display data is in sync with the source
      this.chartData = newData
      this.indexRank(newData)
    }, 715);

  }

  indexRank(data: any[]) {
    this.rankIndex = data.reduce((acc, d) => ({
      ...acc,
      [d[this.valueLabelKey]]: Number(d['rank'])
    }), {});
  }

  decorateWithRectData(data: any[]) {
    const nameColWidth = this.showName ? this.nameColWidth + 'px' : '0px';
    const rankColWidth = this.showRank ? this.rankColWidth + 'px' : '0px';
    const idColWidth = this.showId ? this.idColWidth + 'px' : '0px';
    return data.map((d, i) => {
      return {
        ...d,
        width: this.widthScale(+d[this.valueKey]!) + 'px',
        backgroundColor: this.fillScale(+d[this.valueKey]!).toString(),
        stripeColor: i % 2 === 0 ? '#fff' : '#f5f5f5',
        height: this.useStaticBarHeight ? this.staticBarHeight : (100 / data.length + '%'),
        opacity: 1,
        rank: d['rank'] ?? i + 1,
        idLabel: this.idFormatter ? this.idFormatter(d[this.rowIdKey]) : d[this.rowIdKey],
        nameLabel: this.nameFormatter ? this.nameFormatter(d[this.valueLabelKey], data.length) : d[this.valueLabelKey],
        nameColWidth,
        rankColWidth,
        idColWidth,
        valueColWidth: this.valueColWidth + 'px',
      }
    })
  }

  selectPart(event: ChartRowEvent) {
    const { data, row, col, index, originalEvent } = event
    this.popupController.toggle(originalEvent, data, data[this.rowIdKey])

    if (this.selectedRow == row && this.selectedCol === col) {
      this.selectedRow = null;
      this.selectedRowIndex = null;
      this.selectedCol = null;
      this.popupController.unstick()
      this.onRowUnselected.emit({ row: row!, col: col!, data, originalEvent, index: index! });
    } else {
      this.selectedRow = row;
      this.selectedRowIndex = index;
      this.selectedCol = col;
      this.onRowSelected.emit({ row: row!, col: col!, data, originalEvent, index: index! });
    }

    this.onRowClick.emit({ row: row!, col: col!, data, originalEvent, index: index! });
  }

  handleHover(event: ChartRowEvent) {
    const { data, row, index, originalEvent } = event
    this.hoveredRow = data[this.rowIdKey]
    this.hoveredRowIndex = index
    this.popupController.update(originalEvent, data)

    const emitPayload = { data, row: row!, index: index!, originalEvent }
    this.onRowHover.emit(emitPayload)
  }

  handleHoverMove(event: ChartRowEvent) {
    const { data, row, index, originalEvent } = event
    this.popupController.update(originalEvent, data)
    const emitPayload = { data, row: row!, index: index!, originalEvent }
    this.onRowHoverMove.emit(emitPayload)
  }

  handleUnhover(event: any) {
    this.hoveredRow = null
    this.hoveredRowIndex = null
    this.popupController.hide()
    const emitPayload = { data: undefined, row: null, index: null, originalEvent: event }
    this.onRowUnhover.emit(emitPayload)
  }

  // placeholder value, updated based on the data length
  rowsDisplayed = 1;
  rowSliderMin = 1;
  rowSliderMax = 1;
  rowSliderData = [] as any[]

  updateRowSliderState(newData: any[]) {
    if (this.rowSlider && this.multiSelectParentFilter) {
      const newParentValue = +this.multiSelectParentFilter.value;
      if (this.multiSelectParentValue !== newParentValue) {
        this.rowSliderData = [...newData];
        this.multiSelectParentValue = newParentValue;
        this.rowSliderMax = newData?.length ?? 1;
        this.rowsDisplayed = this.rowSliderMax;
      }
    }
  }

  syncMultiSelectFilterValue() {
    if (!this.rowsDisplayed) {
      return;
    }

    const selectedRowIds = this.rowSliderData
      .slice(0, this.rowsDisplayed)
      .map(d => d[this.rowIdKey])

    this.multiSelectFilter?.setValue(selectedRowIds);
  }
}
